import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {SearchCardGroupsDropdownContainer} from '../dropdowns/search/searchCardGroupsDropdownContainer';
import {OverflowWithCount} from '../overflow/overflowWithCount';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Constants.
 */

const chevronWidth = 32; // px.

/*
 * Props.
 */

interface CardGroupPopoverProps {
  className?: string;
  title: ReactNode;
  cardGroupIds: ReadonlyArray<number>;
  cardGroups: ReadonlyArray<CardGroupModel>;
  namespace: string;

  isDisabled?: boolean;
  isModalInitiallyOpen?: boolean;

  onSelect: HandlerOf<CardGroupModel>;
  onRemove: HandlerOf<CardGroupModel>;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  width: fit-content;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle}
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledSeparatorSpan = styled.span`
  margin-right: 6px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledOverflowDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledPrivateIcon = styled(Icon)`
  margin: 2px 0 0 4px;
`;

const StyledGroupContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

/*
 * Component.
 */

export const CardGroupPopover: FC<CardGroupPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {className, title, isDisabled, cardGroups, isModalInitiallyOpen} = props;
  const [containerRef, {width}] = useMeasureElement();
  return (
    <div ref={containerRef}>
      <CreateFormInputDisplay
        className={className}
        title={title}
        input={
          <PopoverCoordinator
            isDisabled={isDisabled}
            renderButton={() => (
              <StyledButtonDiv $isDisabled={isDisabled}>
                {renderCardGroupsOrPlaceholder(cardGroups, width - chevronWidth)}
                <Icon name="chevronDownSmall" color={theme.greys.shade60} />
              </StyledButtonDiv>
            )}
            renderModal={(popoverProps) => renderSearchGroupsDropdown(popoverProps, props)}
            isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
          />
        }
      />
    </div>
  );
};

/*
 * Helpers.
 */

function renderSearchGroupsDropdown(popoverProps: PopoverTransferProps, props: CardGroupPopoverProps) {
  const {cardGroupIds, namespace, onSelect, onRemove} = props;
  return (
    <SearchCardGroupsDropdownContainer
      popoverProps={popoverProps}
      selectedCardGroupIds={cardGroupIds}
      namespace={namespace}
      onSelect={onSelect}
      onRemove={onRemove}
    />
  );
}

function renderCardGroupsOrPlaceholder(cardGroups: ReadonlyArray<CardGroupModel>, overflowMaxWidth: number) {
  if (cardGroups.length === 0) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledButtonContentDiv>
            <Icon name="listSmall" color={theme.greys.shade60} />
            <StyledPlaceholderTextSpan>
              <Trans id="create-form-input-groups-placeholder">Add to list</Trans>
            </StyledPlaceholderTextSpan>
          </StyledButtonContentDiv>
        )}
      </UserThemeConsumer>
    );
  }

  const renderElement = (group: CardGroupModel, index: number) => (
    <UserThemeConsumer>
      {(theme) => (
        <StyledGroupContainerDiv>
          {group.name}
          {isTeammateNamespace(group.namespace) && (
            <StyledPrivateIcon name="lockFilledNewTiny" color={theme.greys.shade70} />
          )}
          {index === cardGroups.length - 1 ? null : <StyledSeparatorSpan>,</StyledSeparatorSpan>}
        </StyledGroupContainerDiv>
      )}
    </UserThemeConsumer>
  );
  const renderOverflowIndicator = (numberOfHiddenTags: number) => (
    <StyledOverflowDiv>
      <Trans id="create-form-input-groups-additional-hidden-groups-indicator">+{numberOfHiddenTags}</Trans>
    </StyledOverflowDiv>
  );

  return (
    <OverflowWithCount<CardGroupModel>
      elements={cardGroups}
      renderElement={renderElement}
      renderOverflowIndicator={renderOverflowIndicator}
      overflowContainerWidth={overflowMaxWidth}
    />
  );
}
